import {APIClient} from '../utils/APIClient';
import {isOk} from '../utils/apiResponse';
import {API_URL} from '../settings/local_settings';
import {REPORT_TYPE_AD_ACCOUNT_NOT_MONITORED} from '../reports';

// «Inversión sin control» (la solapa `/alerts/sin-control` y la tarjeta gris que lleva a ella desde el
// Tablero, la ficha, el Listado y «Ahora»).
//
// `GET agency/uncontrolled-spend/` → `{status: 'OK', result: {currency, computed_on, snapshot_is_stale,
// totals, counts, scope: {client_id, client_name}, clients: [{client_id, client_name, logo_url, team,
// total_converted, items}], idle, no_client: {count, sample}}}`. Con `?client_id=<id>`, el informe acotado a
// ese cliente (lo arma el backend: la pantalla no filtra nada). Con `?summary=1`, sólo totales y conteos.
// Sin el permiso de la solapa (`uncontrolled_spend_enabled`: toda la 2.1, UD-5), el 404 uniforme.
//
// Lo que no tiene endpoint propio reusa el que ya existe, para que sea la MISMA fila que en las otras
// pantallas:
// - descartar / deshacer → `alert-snooze` (silencio de agencia, el ítem en el sub-indicador);
// - «Crear una línea» → `business-unit/add-bulk` con un ítem sin presupuesto (lo del túnel de alta).
// Las cuentas sin cliente se resuelven en Conexiones (la solapa sólo las cuenta y linkea allá).
//
// ⚠️ `APIClient` no rechaza en 4xx: siempre hay que mirar `status === 'OK'`.
export const DISMISS_CAMPAIGN = 'UNCOVERED_CAMPAIGN';
export const DISMISS_ACCOUNT = 'UNCONTROLLED_ACCOUNT';
const FOREVER = 'FOREVER';
const REPORT_PATH = 'agency/uncontrolled-spend/';

/** La query del informe: `summary=1` y `client_id`, sólo los que vienen. */
export function reportQuery({clientId = null, summary = false} = {}) {
    const params = [];
    if (summary) params.push('summary=1');
    if (clientId !== null && clientId !== undefined && clientId !== '') {
        params.push(`client_id=${encodeURIComponent(clientId)}`);
    }
    return params.length ? `?${params.join('&')}` : '';
}

export function subIndicatorOf({channel, accountId, campaignId}) {
    return campaignId ? `${channel}||${accountId}||${campaignId}` : `${channel}||${accountId}`;
}

// Lo que cambia el informe borra el resumen cacheado: si no, la tarjeta (Tablero, ficha, Listado, «Ahora»)
// seguía mostrando el total de antes hasta 30 s después de resolver algo. `clearCache` borra por substring:
// también los resúmenes por cliente (`?summary=1&client_id=`).
function changing(promise) {
    return promise.then((response) => {
        if (isOk(response)) APIClient.clearCache(REPORT_PATH);
        return response;
    });
}

export const UncontrolledSpendApi = {
    get({clientId = null} = {}) {
        return new APIClient(`${API_URL}${REPORT_PATH}${reportQuery({clientId})}`).call('GET');
    },

    // La tarjeta gris (Tablero, ficha, Listado, «Ahora»): el cache de 30 s evita pedirlo de nuevo al pasar
    // de una pantalla a otra. Con `clientId`, el resumen de ese cliente (otra URL, otro cache).
    summary({clientId = null} = {}) {
        return new APIClient(`${API_URL}${REPORT_PATH}${reportQuery({clientId, summary: true})}`)
            .call('GET', {}, {cache: true});
    },

    // «Sumar las N»: el backend suma TODAS las campañas de la cuenta que ninguna línea mira (menos las
    // descartadas). Con los ids de la tarjeta se sumaban sólo las que muestra, hasta 100.
    addAllToLine(buccId) {
        return changing(new APIClient(`${API_URL}${REPORT_PATH}add-campaigns/`)
            .call('POST', {bucc_id: buccId, all_uncovered: true}));
    },

    notify({clientId, channel, accountId, userIds, note}) {
        return changing(new APIClient(`${API_URL}${REPORT_PATH}notify/`).call('POST', {
            client_id: clientId, channel, account_id: accountId, user_ids: userIds, note,
        }));
    },

    dismiss({indicator, channel, accountId, campaignId = null, reason = null}) {
        const body = {indicator, sub_indicator: subIndicatorOf({channel, accountId, campaignId}), period: FOREVER};
        if (reason) body.reason = reason;
        return changing(new APIClient(`${API_URL}alert-snooze`).call('POST', body));
    },

    undoDismiss({indicator, channel, accountId, campaignId = null}) {
        return changing(new APIClient(`${API_URL}alert-snooze`).call('DELETE', {
            indicator, sub_indicator: subIndicatorOf({channel, accountId, campaignId}),
        }));
    },

    createLine({clientId, channel, accountId}) {
        return changing(new APIClient(`${API_URL}business-unit/add-bulk`)
            .call('POST', [{channel, account_id: String(accountId), agency_client_id: clientId}]));
    },

    // «Recibir este resumen por mail cada mañana» (tanda «Control de líneas», 15/10). El mail diario es el
    // `UserReport` AD_ACCOUNT_NOT_MONITORED de quien mira: el MISMO interruptor que «Informes» de «Mis
    // alertas», por su mismo endpoint. `GET` → `{status: 'OK', data: [tipos activos]}`; el `POST` es
    // idempotente (activar uno activo o apagar uno que no existe contesta OK).
    reportSubscription(userId) {
        return new APIClient(`${API_URL}user/${encodeURIComponent(userId)}/module/marketing/reports`).call('GET');
    },

    setReportSubscription(userId, active) {
        const action = active ? 'activate' : 'deactivate';
        return new APIClient(`${API_URL}user/${encodeURIComponent(userId)}/module/marketing/reports/`
            + `${REPORT_TYPE_AD_ACCOUNT_NOT_MONITORED}/${action}`).call('POST');
    },
};
